import { useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useMessageEditors, useDeleteMessageEditor, ACTION_TYPES, errMessage, type MessageEditorRow } from './systemMasters';
import { MessageEditorDetailDialog } from './MessageEditorDetailDialog';

interface Props { onHome?: () => void; }

const actionName = (v: number) => ACTION_TYPES.find((a) => a.v === v)?.t ?? String(v);

/**
 * Settings ▸ System Management ▸ Message Editor — LIMS MessageEditor_ListView.
 * CRUD list of application messages (MSID auto-generated). Columns MSID / Message /
 * MessageKey / ActionType; New / Edit / Delete / Refresh / Export / Search. The detail
 * form (Module/BO cascade, ActionType, Remark, Translations) opens via Edit / the pencil.
 */
export function MessageEditorPage({ onHome }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const { data: rows = [], isFetching, refetch } = useMessageEditors();
  const del = useDeleteMessageEditor();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [editId, setEditId] = useState<string | null | undefined>(undefined);   // undefined=closed, null=New, guid=Edit
  const cardRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.msid, r.message, r.messageKey, actionName(r.actionType)]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one message to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} message${selectedIds.size === 1 ? '' : 's'}?` })) return;
    try {
      for (const id of selectedIds) await del.mutateAsync(String(id));
      setSelectedIds(new Set());
      await alert({ title: 'Success', message: 'Deleted successfully.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  // LIMS MessageEditor_ListView shows NO sort symbol on any column (filter-only); rows default to MSID order.
  const columns: AppColumn<MessageEditorRow>[] = [
    { field: 'edit', header: '', sortable: false, filterable: false, width: 40, align: 'center',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); setEditId(r.id); }}><i className="ti ti-edit" /></button> },
    { field: 'msid', header: 'MSID', width: '8%', minWidth: 70, sortable: false, text: (r) => r.msid ?? '',
      compare: (a, b) => (a.msid ?? '').localeCompare(b.msid ?? '', undefined, { numeric: true }) },
    { field: 'message', header: 'Message', width: '46%', minWidth: 240, sortable: false, text: (r) => r.message ?? '' },
    { field: 'messageKey', header: 'MessageKey', width: '26%', minWidth: 140, sortable: false, text: (r) => r.messageKey ?? '' },
    { field: 'actionType', header: 'ActionType', width: '20%', minWidth: 120, sortable: false, text: (r) => actionName(r.actionType) },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Message Editor</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-message-editor" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Message Editor" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onNew={() => setEditId(null)}
          onEdit={() => { const id = [...selectedIds][0]; if (id) setEditId(String(id)); }}
          editDisabled={selectedIds.size !== 1}
          onDelete={() => void doDelete()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="MessageEditor" headers={['MSID', 'Message', 'MessageKey', 'ActionType']}
                rows={visible.map((r) => [r.msid ?? '', r.message ?? '', r.messageKey ?? '', actionName(r.actionType)])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (BO UI Standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<MessageEditorRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            initialSort={{ key: 'msid', dir: 'asc' }} loading={isFetching} emptyText="No messages found." />
        </div>
      </div>

      {editId !== undefined && (
        <MessageEditorDetailDialog id={editId} cardRef={cardRef} onClose={() => setEditId(undefined)} />
      )}
    </div>
  );
}
